import React, { useState } from "react";
import {
  ShoppingBag,
  Category,
  DateRange,
  Engineering,
  Payment,
  Star,
} from "@mui/icons-material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import moment from "moment";
import "moment/locale/ar";
import "moment/locale/en-gb";
import Stepper from "../Stepper/Stepper";
import { useLanguage } from "../../context/LanguageContext";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import GreenButton from "../GreenButton/GreenButton";
import RatingComponent from "../RatingComponent/RatingComponent";
import api from "../../utils/api";
import { photosApi } from "../../SourceData";

const OrderCard = ({ order, onCancelOrder, onRateOrder }) => {
  const { language, t } = useLanguage();
  const [openModal, setOpenModal] = useState(false);
  const [cancelConfirmed, setCancelConfirmed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [openRatingModal, setOpenRatingModal] = useState(false);

  // Format date using moment with appropriate locale
  const formatDate = (dateString) => {
    try {
      return moment(dateString).locale(language).format("DD/MM/YYYY");
    } catch (error) {
      console.error("Error formatting date:", error);
      return language === "ar" ? "تاريخ غير صالح" : "Invalid Date";
    }
  };

  // Format time to 12-hour format with AM/PM or صباحا/مساء indicator
  const formatTime = (timeString) => {
    try {
      // Parse the time (assuming it's in 24-hour format like "11:00")
      const [hours, minutes] = timeString.split(":");
      const hourNum = parseInt(hours, 10);

      // Convert to 12-hour format
      let hour12 = hourNum % 12;
      if (hour12 === 0) hour12 = 12; // Convert 0 to 12 for 12 AM/PM

      // Determine period
      const isPM = hourNum >= 12;

      // Format based on language
      if (language === "ar") {
        const period = isPM ? "مساءً" : "صباحاً";
        return `${hour12}:${minutes} ${period}`;
      } else {
        const period = isPM ? "PM" : "AM";
        return `${hour12}:${minutes} ${period}`;
      }
    } catch (error) {
      console.error("Error formatting time:", error);
      return timeString; // Return original if there's an error
    }
  };

  // Get payment method with localization
  const getPaymentMethod = () => {
    if (order?.isPackage === true) return t("subscriptionPackage");
    if (!order?.paymentMethod) return t("notSpecified");
    return t(order.paymentMethod);
  };

  // Extract department name based on language
  const getDepartmentName = () => {
    if (!order.technician?.category?.name) return t("notSpecified");

    // If the category has language-specific names, use them
    if (language === "ar" && order.technician.category.name.ar) {
      return order.technician.category.name.ar;
    } else if (language === "en" && order.technician.category.name.en) {
      return order.technician.category.name.en;
    }

    // Fallback to whatever is available
    return (
      order.technician.category.name.ar ||
      order.technician.category.name.en ||
      order.technician.category.name ||
      t("notSpecified")
    );
  };

  // Format the total price
  const totalPrice = order.totalPrice?.totalPrice || 0;
  const currencySymbol = language === "ar" ? "ر.س" : "SAR";

  // Calculate hours until appointment
  const calculateTimeUntilAppointment = () => {
    const now = moment();
    const appointmentDate = moment(order.date);
    const appointmentTime = order.time.from.split(":");
    appointmentDate.hour(parseInt(appointmentTime[0]));
    appointmentDate.minute(parseInt(appointmentTime[1]));

    // Get the difference in hours
    const hoursUntilAppointment = appointmentDate.diff(now, "hours");
    return hoursUntilAppointment;
  };

  // Determine refund or package credit information based on cancellation time
  const getRefundInfo = () => {
    const hoursUntilAppointment = calculateTimeUntilAppointment();
    // Different logic for subscription packages
    if (order?.isPackage === true) {
      if (hoursUntilAppointment >= 24) {
        return {
          percentage: 0,
          isPackage: true,
          noVisitDeducted: true,
          message: t("packageNoVisitDeductedMessage"),
        };
      } else {
        return {
          percentage: 0,
          isPackage: true,
          noVisitDeducted: false,
          message: t("packageVisitDeductedMessage"),
        };
      }
    }
    // Regular payment refund logic
    else {
      if (hoursUntilAppointment >= 48) {
        return {
          percentage: 100,
          isPackage: false,
          message: t("fullRefundMessage"),
        };
      } else if (hoursUntilAppointment >= 12 && hoursUntilAppointment < 24) {
        return {
          percentage: 50,
          isPackage: false,
          message: t("partialRefundMessage"),
        };
      } else {
        return {
          percentage: 0,
          isPackage: false,
          message: t("noRefundMessage"),
        };
      }
    }
  };

  const refundInfo = getRefundInfo();

  // Handle modal open
  const handleOpenModal = () => {
    setOpenModal(true);
    setCancelError(null);
  };

  // Handle modal close
  const handleCloseModal = () => {
    setOpenModal(false);
    setCancelConfirmed(false);
    setCancelError(null);
  };

  // Handle cancel confirmation
  const handleCancelConfirmation = async () => {
    setIsSubmitting(true);
    setCancelError(null);

    try {
      // Call the API to cancel the booking
      await api.patch(`/bookings/${order._id}/cancel`);

      // If successful, update the UI
      setCancelConfirmed(true);

      // Call the parent component's callback if provided
      if (onCancelOrder && typeof onCancelOrder === "function") {
        onCancelOrder(order._id, refundInfo.percentage);
      }
    } catch (error) {
      console.error("Error cancelling order:", error);
      setCancelError(
        error.response?.data?.message ||
          t("cancelOrderErrorMessage") ||
          "An error occurred while cancelling the order."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle rating button click
  const handleRateOrder = () => {
    setOpenRatingModal(true);
  };

  // Handle rating submission success
  const handleRatingSubmitted = (bookingId, rating, comment) => {
    // Call the parent component's callback if provided
    if (onRateOrder && typeof onRateOrder === "function") {
      onRateOrder(bookingId, rating, comment);
    }

    // Close the rating modal
    setOpenRatingModal(false);
  };

  // Define enhanced modal style
  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: {
      xs: "90%", // Full width on extra small devices minus margin
      sm: "80%", // 80% width on small devices
      md: 600, // Fixed width on medium devices and up
      lg: 700, // Larger fixed width on large devices
    },
    maxWidth: 800,
    maxHeight: "90vh",
    overflow: "auto",
    bgcolor: "background.paper",
    border: "2px solid #5cb85c",
    borderRadius: "12px",
    boxShadow: 24,
    p: { xs: 2, sm: 3, md: 4 },
    direction: language === "ar" ? "rtl" : "ltr",
  };

  // Get the last state of the order
  const getLastState = () => {
    if (!order.status || order.status.length === 0) return null;
    // Sort status array by date to get the most recent status
    const sortedStatus = [...order.status].sort(
      (a, b) => new Date(b.date) - new Date(a.date)
    );
    return sortedStatus[0]?.value;
  };

  const lastState = getLastState();

  // Check if cancel button should be shown
  const shouldShowCancelButton = lastState === "booked";

  // Check if rating button should be shown
  const shouldShowRatingButton =
    lastState === "completed" && order.isRated !== true;

  return (
    <div className="item" key={order._id}>
      <div className="itme-details">
        <div className="item-texts">
          <div className="item-header">
            <div className="action-buttons">
              {shouldShowCancelButton && (
                <div
                  className={`cancel-button ${
                    language === "ar" ? "right-aligned" : "left-aligned"
                  }`}
                >
                  <GreenButton
                    text={t("cancelOrder")}
                    variant="cancel"
                    size={language === "ar" ? "medium" : "large"}
                    onClick={handleOpenModal}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="text">
            <Engineering sx={{ width: 19, height: 19, color: "#5cb85c" }} />
            <span>
              {t("technicianName")}{" "}
              {order.technician?.name || t("notSpecified")}
            </span>
          </div>
          <div className="text">
            <DateRange sx={{ width: 19, height: 19, color: "#5cb85c" }} />
            <span>
              {t("appointmentDate")} {formatDate(order.date)}
            </span>
          </div>
          <div className="text">
            <AccessTimeIcon sx={{ width: 19, height: 19, color: "#5cb85c" }} />
            <span>
              {t("appointmentTime")} {formatTime(order.time.from)} -{" "}
              {formatTime(order.time.to)}
            </span>
          </div>
          <div className="text">
            <Category sx={{ width: 19, height: 19, color: "#5cb85c" }} />
            <span>
              {t("department")} {getDepartmentName()}
            </span>
          </div>
          {order.services?.length > 0 && (
            <div className="text">
              <ShoppingBag sx={{ width: 19, height: 19, color: "#5cb85c" }} />
              <span>
                {t("servicesCount")} {order.services?.length || 0}
              </span>
            </div>
          )}

          <div className="text">
            <Payment sx={{ width: 19, height: 19, color: "#5cb85c" }} />
            <span>
              {t("paymentMethod")} {getPaymentMethod()}
            </span>
          </div>
          <p>
            {t("orderNumber")} {order.orderNumber}
          </p>
          {order.isPackage !== true && (
            <div className="total">
              {t("total")} {totalPrice} {currencySymbol}
            </div>
          )}
          {shouldShowRatingButton && (
            <div
              className={`rating-button ${
                language === "ar" ? "right-aligned" : "left-aligned"
              }`}
            >
              <GreenButton
                text={t("rateService")}
                variant="fill"
                size={language === "ar" ? "medium" : "large"}
                onClick={handleRateOrder}
                icon={<Star sx={{ width: 16, height: 16 }} />}
              />
            </div>
          )}
        </div>
        <div className="technician-img">
          <img src={photosApi + order.technician.photo} alt="" />
        </div>
      </div>
      <div className="item-status">
        <Stepper statusArray={order.status} />
      </div>

      {/* Enhanced Cancellation Modal */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="cancel-order-modal"
        aria-describedby="cancel-order-details"
      >
        <Box sx={modalStyle}>
          <Typography
            id="cancel-order-modal"
            variant="h6"
            component="h2"
            sx={{
              mb: 3,
              fontWeight: "bold",
              color: "#220e5f",
              textAlign: "center",
              borderBottom: "1px solid #eaeaea",
              pb: 2,
            }}
          >
            {cancelConfirmed ? t("orderCancelledTitle") : t("cancelOrderTitle")}
          </Typography>

          {cancelError && (
            <Box
              sx={{
                mb: 3,
                bgcolor: "#ffebee",
                p: 2,
                borderRadius: 1,
                borderLeft: "4px solid #f44336",
              }}
            >
              <Typography variant="body2" color="error">
                {cancelError}
              </Typography>
            </Box>
          )}

          {!cancelConfirmed ? (
            <>
              <Typography
                variant="body1"
                sx={{
                  mb: 3,
                  bgcolor: "#f8f9fa",
                  p: 2,
                  borderRadius: 1,
                  borderLeft: "4px solid #5cb85c",
                }}
              >
                {refundInfo.message}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  mb: 2,
                  fontWeight: "bold",
                  color: "#220e5f",
                }}
              >
                {t("refundTermsTitle")}:
              </Typography>

              <Box sx={{ pl: 2 }}>
                {order?.isPackage === true ? (
                  <>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                      1. {t("packageTerm1")}
                    </Typography>

                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                      2. {t("packageTerm2")}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                      1. {t("refundTerm1")}
                    </Typography>

                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                      2. {t("refundTerm2")}
                    </Typography>

                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                      3. {t("refundTerm3")}
                    </Typography>
                  </>
                )}
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mt: 3,
                  gap: 2,
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <GreenButton
                  text={t("goBack")}
                  variant="fill"
                  size={language === "ar" ? "medium" : "large"}
                  onClick={handleCloseModal}
                  disabled={isSubmitting}
                />

                <GreenButton
                  text={isSubmitting ? t("cancelling") : t("confirmCancel")}
                  variant="cancel"
                  size={language === "ar" ? "medium" : "large"}
                  onClick={handleCancelConfirmation}
                  disabled={isSubmitting}
                />
              </Box>
            </>
          ) : (
            <>
              <Box
                sx={{
                  bgcolor: "#f8f9fa",
                  p: 3,
                  borderRadius: 2,
                  borderLeft: "4px solid #5cb85c",
                  mb: 3,
                }}
              >
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {t("cancelConfirmationMessage")}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                  {refundInfo.isPackage ? (
                    <span>
                      {refundInfo.noVisitDeducted
                        ? t("packageNoVisitDeductedConfirmMessage")
                        : t("packageVisitDeductedConfirmMessage")}
                    </span>
                  ) : refundInfo.percentage > 0 ? (
                    <span>
                      {t("refundPercentageMessage").replace(
                        "{percentage}",
                        refundInfo.percentage
                      )}
                    </span>
                  ) : (
                    <span>{t("noRefundAppliedMessage")}</span>
                  )}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <GreenButton
                  text={t("close")}
                  variant="fill"
                  size={language === "ar" ? "medium" : "large"}
                  onClick={handleCloseModal}
                />
              </Box>
            </>
          )}
        </Box>
      </Modal>

      {/* Rating Modal */}
      <RatingComponent
        open={openRatingModal}
        onClose={() => setOpenRatingModal(false)}
        order={order}
        onRatingSubmitted={handleRatingSubmitted}
      />
    </div>
  );
};

export default OrderCard;
